'use client'

import { clsx } from 'clsx'
import { type FC, useRef } from 'react'

import { PimVariantAttributeV1TypeEnum } from '@redteclab/api/clients/bully'

import { Link } from 'base-ui'

import { useExperimentsContext } from '../../../../../experiments/components/ExperimentsContext'
import { experimentAccessorIsMarketplace } from '../../../../../experiments/model/experimentAccessor'
import { useGlobalConfigContext } from '../../../../../global-config'
import { ATTRIBUTE_NAME_PACKAGE_SIZE } from '../../../product-page-context/ATTRIBUTE_NAME'
import { type AttributeGroupData } from '../../../product-page-context/AttributeGroup'
import { useProductPageContext } from '../../../productPageContext'
import { ProductAttributeTileWithPrice } from '../ProductAttributeTileWithPrice'
import { ProductPageAttributeHead } from '../ProductPageAttributeHead'
import { productPageGetAttributeSelectionEntryConditions } from '../ProductPageAttributeSelector/productPageGetAttributeSelectionEntryConditions'
import { ProductPageColorTypeAttributeHead } from '../ProductPageColorTypeAttributeGroup/ProductPageColorTypeAttributeHead'
import { useAttributeSelectionCollapsible } from '../useAttributeSelectionCollapsible'
import { useScrollElementIntoView } from '../useScrollElementIntoView'

import { ProductAttributeTileWithPriceDescription } from './ProductAttributeTileWithPriceDescription'
import { useSelectionSetAnyDiscountOrBonuses } from './useSelectionSetAnyDiscountOrBonuses'

type ProductAttributeTileWithPriceGroupProps = {
  attributeGroup: AttributeGroupData
  isSingleGroup: boolean
}

const MAX_TILES_PER_ROW = 4

export const ProductAttributeTileWithPriceGroup: FC<
  ProductAttributeTileWithPriceGroupProps
> = ({ attributeGroup, isSingleGroup }) => {
  const { tenant } = useGlobalConfigContext()
  const { currentPrescriptionType, offerId } = useProductPageContext()
  const { experiments } = useExperimentsContext()
  const isMarketplace = experimentAccessorIsMarketplace(experiments, tenant)
  const { discountBoundaries } = useGlobalConfigContext()

  const {
    metaAttributeData,
    selectionEntries,
    shouldDisplayOnlySelectedValue,
  } = attributeGroup

  const { hasAnyBonuses, hasAnyDiscounts } =
    useSelectionSetAnyDiscountOrBonuses({
      currentPrescriptionType,
      discountBoundaries,
      isMarketplace,
      offerId,
      selectionEntries,
    })

  const selectedEntry = selectionEntries.find((it) => it.isSelected)
  const isColorVariant =
    metaAttributeData.type === PimVariantAttributeV1TypeEnum.Color
  const isPackageSize =
    metaAttributeData.variant_attribute === ATTRIBUTE_NAME_PACKAGE_SIZE

  const AttributeHeadComponent = isColorVariant
    ? ProductPageColorTypeAttributeHead
    : ProductPageAttributeHead

  const scrollableElementRef = useRef(null)
  const handleScrollToElement = useScrollElementIntoView(scrollableElementRef)

  const collapsibleData = useAttributeSelectionCollapsible({
    isSingleGroup,
    metaAttributeData,
    selectionEntries,
  })

  return (
    <div>
      <AttributeHeadComponent
        metaAttribute={metaAttributeData}
        selectedEntry={selectedEntry}
      />
      {!shouldDisplayOnlySelectedValue && (
        <>
          <div
            className={clsx(
              'flex flex-nowrap gap-x-2.5 overflow-auto scrollbar-hide tablet:flex-wrap tablet:scrollbar-default',
              isPackageSize &&
                'tablet:grid tablet:grid-cols-[repeat(auto-fit,minmax(102px,1fr))] desktop:grid-cols-4',
              selectionEntries.length >= MAX_TILES_PER_ROW &&
                'tablet:justify-between',
            )}
            ref={scrollableElementRef}
          >
            {selectionEntries.map((entry, index) => {
              const conditions =
                productPageGetAttributeSelectionEntryConditions(entry)
              const { candidateVariant } = conditions
              const { isSelected } = entry

              if (!candidateVariant) {
                return null
              }

              return (
                <ProductAttributeTileWithPrice
                  attributeDescription={
                    <ProductAttributeTileWithPriceDescription
                      selectionEntry={entry}
                    />
                  }
                  candidateVariant={candidateVariant}
                  className={clsx(
                    collapsibleData.maxVisibleIndex <= index && 'tablet:hidden',
                  )}
                  hasAnyBonuses={hasAnyBonuses}
                  hasAnyDiscounts={hasAnyDiscounts}
                  isVariantSelected={isSelected}
                  key={candidateVariant.upid}
                  onScrollIntoView={handleScrollToElement}
                  selectionEntry={entry}
                  // only packageSize shown with small tiles
                  size={isPackageSize ? 'small' : 'large'}
                />
              )
            })}
          </div>
          {collapsibleData.showLabel ? (
            <Link
              className="mt-3 hidden w-full flex-row items-center justify-center tablet:flex"
              color="brand"
              onClick={collapsibleData.handleLabelClick}
              slots={{ root: 'button' }}
              type="button"
            >
              {collapsibleData.labelContent}
            </Link>
          ) : null}
        </>
      )}
    </div>
  )
}
